import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CreateIdvSessionBody,
  IdvPsc,
  IdvSession,
  IdvSessionStatus,
} from '@pflegenavi/shared/api';
import type { IdvPscAction } from '@pflegenavi/shared-frontend/platform';
import {
  getIdvActions,
  idvSessionLanguage,
  kycRenewalDaysLeft,
} from '@pflegenavi/shared-frontend/platform';
import { useErrorSnackbar } from '../snackbars/useErrorSnackbar';
import type { KycIdvSubject } from './KycIdvDialogView';
import { KycIdvDialogView } from './KycIdvDialogView';

export interface KycIdvDialogProps {
  open: boolean;
  onClose: () => void;
  /** Shown under the title, e.g. the legal entity name. */
  subtitle?: string;
  /** Selects what an unverified subject is blocked from. */
  subject: KycIdvSubject;
  kycVerified: boolean;
  renewalDeadline: string | null;
  lastKycDate: string | null;
  /** Persisted main session status; the live session details take precedence. */
  sessionStatus: IdvSessionStatus | null;
  session: IdvSession | undefined;
  sessionLoading: boolean;
  returnUrl: string;
  onStartSession: (body: CreateIdvSessionBody) => Promise<IdvSession>;
  onRetryPsc: (pscId: string) => Promise<IdvPsc>;
  onRedirect?: (url: string) => void;
}

const redirectToMangopay = (url: string) => window.location.assign(url);

export const KycIdvDialog: FC<KycIdvDialogProps> = ({
  renewalDeadline,
  sessionStatus,
  session,
  sessionLoading,
  returnUrl,
  onStartSession,
  onRetryPsc,
  onRedirect = redirectToMangopay,
  ...viewProps
}) => {
  const { t, i18n } = useTranslation();
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const [pending, setPending] = useState(false);

  const status = session?.status ?? sessionStatus;
  const waitingForSession =
    Boolean(sessionStatus) && !session && sessionLoading;
  const actions = getIdvActions(status, session?.pscs ?? []);

  const redirectTo = useCallback(
    async (getUrl: () => Promise<string | null>) => {
      setPending(true);
      try {
        const url = await getUrl();
        if (!url) {
          throw new Error('MangoPay returned no hosted URL');
        }
        onRedirect(url);
      } catch (error) {
        enqueueErrorSnackbar(error, t('kyc.idv.errors.redirect-failed'));
        setPending(false);
      }
    },
    [enqueueErrorSnackbar, onRedirect, t]
  );

  const handlePrimaryAction = useCallback(
    () =>
      redirectTo(async () =>
        actions.primary === 'continue'
          ? session?.hosted_url ?? null
          : (
              await onStartSession({
                return_url: returnUrl,
                session_language: idvSessionLanguage(i18n.language),
              })
            ).hosted_url
      ),
    [
      actions.primary,
      i18n.language,
      onStartSession,
      redirectTo,
      returnUrl,
      session,
    ]
  );

  const handlePscAction = useCallback(
    ({ psc, action }: IdvPscAction) =>
      redirectTo(async () =>
        action === 'open'
          ? psc.hosted_url
          : (await onRetryPsc(psc.id)).hosted_url
      ),
    [onRetryPsc, redirectTo]
  );

  return (
    <KycIdvDialogView
      {...viewProps}
      renewalDeadline={renewalDeadline}
      renewalDaysLeft={kycRenewalDaysLeft(renewalDeadline, new Date())}
      sessionStatus={status}
      session={session}
      waitingForSession={waitingForSession}
      actions={actions}
      pending={pending}
      onPrimaryAction={handlePrimaryAction}
      onPscAction={handlePscAction}
    />
  );
};
